import React from 'react';
import { motion } from 'framer-motion';
import { Activity, Bell, Moon, Sun, UserCircle2, Wifi, WifiOff } from 'lucide-react';
import { useMQTT } from '../context/MQTTContext';
import { useNavigate } from 'react-router-dom';

export default function MobileHeader({ theme, onToggleTheme }) {
  const { connected, status, sensorData } = useMQTT();
  const navigate = useNavigate();

  const unread =
    (sensorData.temperature > 40 ? 1 : 0) +
    (sensorData.humidity > 85 ? 1 : 0) +
    (sensorData.vibration > 15 ? 1 : 0) +
    (sensorData.gas > 0.8 ? 1 : 0) +
    (sensorData.battery < 30 ? 1 : 0);

  return (
    <header className="sticky top-0 z-40 px-3 sm:px-5 lg:px-8 pt-3 sm:pt-4">
      <div className="max-w-7xl mx-auto">
        <div className="card-surface rounded-2xl flex items-center gap-3 px-3 sm:px-4 py-2.5">
          {/* Logo */}
          <motion.button
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5"
            whileTap={{ scale: 0.96 }}
          >
            <span className="relative inline-flex">
              <span className="absolute inset-0 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 blur-md opacity-60" />
              <span className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 grid place-items-center shadow-glow">
                <Activity className="w-5 h-5 text-white" />
              </span>
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="text-[15px] font-bold tracking-tight">Container Monitor</span>
              <span className="text-[11px] opacity-60 font-medium">AI · Realtime</span>
            </span>
            <span className="sm:hidden text-[14px] font-bold tracking-tight">CM·AI</span>
          </motion.button>

          {/* Live status pill */}
          <div className="ml-auto flex items-center gap-2">
            <motion.div
              layout
              className={`chip ${
                connected
                  ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                  : 'bg-red-500/15 text-red-600 dark:text-red-400'
              }`}
            >
              <span className="relative inline-flex w-2 h-2">
                <span
                  className={`absolute inset-0 rounded-full ${
                    connected ? 'bg-emerald-500 animate-pulse-soft' : 'bg-red-500'
                  }`}
                />
              </span>
              {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              <span className="hidden xs:inline sm:inline">{connected ? 'Live' : status}</span>
            </motion.div>

            {/* Notifications */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate('/alerts')}
              className="relative w-10 h-10 grid place-items-center rounded-xl hover:bg-black/5 dark:hover:bg-white/5"
              aria-label="Alerts"
            >
              <Bell className="w-5 h-5" />
              {unread > 0 && (
                <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-red-500 text-white text-[10px] font-bold">
                  {unread}
                </span>
              )}
            </motion.button>

            {/* Theme toggle */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={onToggleTheme}
              className="w-10 h-10 grid place-items-center rounded-xl hover:bg-black/5 dark:hover:bg-white/5"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </motion.button>

            {/* Profile */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate('/settings')}
              className="w-10 h-10 grid place-items-center rounded-xl hover:bg-black/5 dark:hover:bg-white/5"
              aria-label="Profile"
            >
              <UserCircle2 className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </header>
  );
}
